// pages/coordination.js — Coordination page (decisions + messages feed).
//
// Owns the `data-page="coordination"` section, which renders into
// `#decisions-body` (the legacy element ID — "Coordination" was
// previously named "Decisions"). A unified team-wide activity feed:
// agent decisions (auto-stubs from threshold-crossing lease releases)
// + cross-chat messages (agc msg send), with a kind filter, full-text
// search, a date-range window, and server-side pagination. Click a
// message row → its thread drawer.
//
// LOCAL + HOSTED parity: renderCloudDecisions drives BOTH modes. The
// fetchers branch on isCloudMode() — local: /v1/decisions +
// /v1/a2a/threads (plaintext); hosted: /v1/teams/decisions +
// /v1/teams/handoffs/threads (encrypted hints + BU filter) — and the
// renderer normalizes both shapes into one row model.
import { state } from "../state.js";
import {
  decisionsState,
  handoffsState,
  coordinationState,
  collabDrillState,
} from "../page-state.js";
import { setBodyHTML, isBodyFirstPaint } from "../dom.js";
import { escapeHTML, ago, formatDuration } from "../util.js";
import { isCloudMode } from "../ui.js";
import { api } from "../api.js";
import { decryptHint } from "../crypto.js";
import { chatLabel, parseAgentID, kindCountLabel, isAgentCollisionID } from "../format.js";
import { navigate, routeURL } from "../router.js";
import { groupDecisionRows, isSessionRecord, sessionFileCount } from "../decision-groups.js";
import {
  fetchDecisions,
  fetchCoordinationDecisions,
  fetchCoordinationMessages,
  fetchCollabItems,
} from "../fetchers.js";
import {
  parseCollabQuery,
  collabQueryString,
  collabFilterLabel,
  collabFilterNote,
  collabRow,
} from "../collab-drill.js";

// ---- Cloud-mode activity feed (Coordination page) ----
//
// In cloud mode the Coordination page becomes a unified team-wide
// activity feed: it pulls decisions, cross-chat messages, and
// (future) policy fires in parallel, merges them by timestamp, and
// renders one feed with a `kind` chip per row. The intent: one page
// where the operator sees "what did the agents on my team actually
// do?" without hunting across separate Decisions / Messages / Policy
// tabs.

// activityFilter: which kinds (decision/message) are shown. The size>1
// guard in the toggle handler prevents de-selecting the LAST active kind,
// so the user can never end up with a dead "all rows hidden" state.
const activityFilter = {
  kinds: new Set(["decision", "message"]),
};

// Loading guard so overlapping ticks don't double-fetch + flicker.
let coordLoading = false;
// First-fetch guard: after the initial load, ticks render from state and
// only an explicit refetch (filter/page change) hits the network — so an
// empty tenant doesn't re-poll every tick.
let coordFetchedOnce = false;

// openThreadDrawer is called when an operator clicks a message row
// in the coordination feed. Navigates to the Messages page (which
// carries the thread-rendering + reply UI we don't want to
// duplicate) and pre-expands the clicked thread.
function openThreadDrawer(threadId) {
  if (!threadId) return;
  handoffsState.returnPage = "coordination";
  navigate("handoffs", true, { threadID: threadId });
}

// shortPath turns a decision's absolute "file:/Users/.../repo/src/x.ts"
// into a readable repo-relative-ish tail (last 2-3 segments). Falls back
// to the raw string for non-file resources.
function shortPath(resource) {
  let s = String(resource || "").replace(/^file:/, "");
  // Lease resources can be "file:<root>:<rel>" — keep the part after the
  // last colon when present (the relative path).
  const colon = s.lastIndexOf(":");
  if (colon > 1) s = s.slice(colon + 1);
  const parts = s.split("/").filter(Boolean);
  if (parts.length <= 3) return s;
  return parts.slice(-3).join("/");
}

// headlineFromBody pulls the first meaningful line out of a decision body
// (its purpose), stripping markdown heading hashes. Mirrors the local
// branch's extraction so both render the same one-liner.
function headlineFromBody(body) {
  const line = String(body || "").split("\n").find((l) => l.trim()) || "";
  return line.replace(/^#+\s*/, "").trim();
}

// SENTINEL shown when an encrypted hint can't be decrypted (team key not
// imported on this browser). Never leave the cell blank.
const ENC_SENTINEL = "(encrypted — import team key)";

// debounce — tiny trailing-edge debounce for the search input.
// agentCell names WHICH chat: the runtime plus its short session id, so
// rows from different chats are told apart (every row used to read just
// "claude-code"). The full id is in the tooltip.
function agentCell(agentID) {
  if (!agentID) return "—";
  if (!agentID.includes("/") || isAgentCollisionID(agentID)) {
    return `<span data-tooltip="${escapeHTML(agentID)}">${escapeHTML(chatLabel(agentID))}</span>`;
  }
  const { tool, shortID } = parseAgentID(agentID);
  return `<span data-tooltip="${escapeHTML(agentID)}">${escapeHTML(tool)} <code style="font-size: 11px; color: var(--text-muted);">${escapeHTML(shortID)}</code></span>`;
}

// Decision rows the user expanded (kept across re-renders and ticks).
const openDecisions = new Set();

function firstLine(text) {
  const t = (text || "").trim().split("\n").find((l) => l.trim()) || "";
  return t.length > 160 ? t.slice(0, 157) + "…" : t;
}

// readableRecord strips the markdown scaffolding a decision file carries
// ("# heading", the "To replace with a richer description, run: agc
// decision ..." footer) so the opened record reads as plain text.
function readableRecord(text) {
  return (text || "")
    .split("\n")
    // ">"-quoted lines are the auto-stub's how-to-edit note, and "held for"
    // repeats the duration shown above the record.
    .filter((l) => !/^\s*>/.test(l) && !/^\s*held for \S+\s*$/i.test(l))
    .map((l) => l.replace(/^#+\s*/, ""))
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function chatName(agentID) {
  return chatLabel(agentID);
}

function debounce(fn, ms) {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

const WINDOW_OPTIONS = [
  { value: "24h", label: "Last 24h" },
  { value: "168h", label: "Last 7d" },
  { value: "720h", label: "Last 30d" },
  { value: "", label: "All time" },
];

// pagerHTML — Prev/Next pager for one source (decisions OR messages).
// kind is "decision"|"message"; page is 0-based; total is the server's
// total_rows. Buttons carry data-coord-page="<kind>:<first|prev|next|last>".
function pagerHTML(kind, page, total, pageSize) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return "";
  const p = Math.min(Math.max(0, page), totalPages - 1);
  const start = p * pageSize;
  const end = Math.min(start + pageSize, total);
  const prevOff = p <= 0 ? "disabled" : "";
  const nextOff = p >= totalPages - 1 ? "disabled" : "";
  return `<div class="pagination" style="padding: 8px 16px;">
    <div class="info">${kind === "decision" ? "Decisions" : "Messages"}: <strong style="color: var(--text);">${(start + 1).toLocaleString()}&ndash;${end.toLocaleString()}</strong> of <strong style="color: var(--text);">${total.toLocaleString()}</strong></div>
    <div class="controls">
      <button data-coord-page="${kind}:first" ${prevOff}>&laquo;</button>
      <button data-coord-page="${kind}:prev"  ${prevOff}>&lsaquo; Prev</button>
      <span class="page-indicator">${p + 1} / ${totalPages}</span>
      <button data-coord-page="${kind}:next"  ${nextOff}>Next &rsaquo;</button>
      <button data-coord-page="${kind}:last"  ${nextOff}>&raquo;</button>
    </div>
  </div>`;
}

// loadCoordination fetches whichever active sources need data, then
// re-renders. Decisions and messages page independently (server-side).
async function loadCoordination() {
  const tasks = [];
  if (activityFilter.kinds.has("decision")) tasks.push(fetchCoordinationDecisions());
  if (activityFilter.kinds.has("message")) tasks.push(fetchCoordinationMessages());
  await Promise.allSettled(tasks);
}

// renderCloudDecisions — unified, server-paginated activity feed (cloud).
async function renderCloudDecisions(opts) {
  const body = document.getElementById("decisions-body");
  if (!body) return;
  const force = !!(opts && opts.force);
  const refetch = !!(opts && opts.refetch);

  // Fetch on an explicit refetch (filter/page change) or on the FIRST paint
  // only. After the first fetch we render from state on ticks — a genuinely
  // empty tenant must not re-poll every ~5s tick. A filter/page change always
  // passes refetch:true.
  const needFetch = refetch || !coordFetchedOnce;
  if (needFetch && !coordLoading) {
    coordLoading = true;
    coordFetchedOnce = true;
    try {
      await loadCoordination();
    } finally {
      coordLoading = false;
    }
  }

  // Map + decrypt decision rows. Hosted sends encrypted resource_hint/
  // body_hint; local (non-cloud) sends plaintext resource/body.
  const decisionRows = activityFilter.kinds.has("decision")
    ? coordinationState.decisions
    : [];
  const decisions = await Promise.all(
    decisionRows.map(async (r) => {
      const resource = r.resource_hint
        ? (await decryptHint(r.resource_hint)) || ENC_SENTINEL
        : r.resource || "";
      const bodyText = r.body_hint
        ? (await decryptHint(r.body_hint)) || ""
        : r.body || "";
      const purpose = headlineFromBody(bodyText);
      const path = shortPath(resource);
      let summary = purpose ? `${path} · ${purpose}` : path || "(decision)";
      // A session record speaks for the whole chat: lead with how many
      // files and what it did, not the one file it happened to end on.
      if (isSessionRecord(r)) {
        const n = sessionFileCount(bodyText);
        summary = (n ? `${n} file${n === 1 ? "" : "s"} · ` : "") + (purpose || "session");
      }
      return {
        kind: "decision",
        generated: !!r.generated,
        purpose,
        path,
        ts: r.released_at || r.acquired_at || "",
        agent_id: r.agent_id || "",
        machine_id: r.machine_id || "",
        subsystem: r.subsystem || "",
        summary,
        detail: bodyText || resource,
        resource,
        // Local rows carry a formatted duration; hosted rows carry duration_ms.
        duration: r.duration || (r.duration_ms > 0 ? formatDuration(Math.round(r.duration_ms / 1000)) : ""),
        lease_id: r.lease_id || "",
        thread_id: "",
      };
    }),
  );

  // Map message rows. The two shapes share most keys (last_from / last_to /
  // context_id / last_at); they differ only on the subject/body, which the
  // HOSTED endpoint sends encrypted (last_subject_hint / last_body_hint) and
  // the LOCAL endpoint sends plaintext (subject / last_body). Local is
  // single-machine, so last_from_machine is absent there.
  const messageRows = activityFilter.kinds.has("message")
    ? coordinationState.messages
    : [];
  const messages = await Promise.all(
    messageRows.map(async (m) => {
      const subject = m.last_subject_hint
        ? (await decryptHint(m.last_subject_hint)) || ENC_SENTINEL
        : m.subject || "(no subject)";
      const bodyText = m.last_body_hint
        ? (await decryptHint(m.last_body_hint)) || ""
        : m.last_body || "";
      // Lead with what was said: agc msg send has no subject, so the old
      // "(no subject) — from claude-code → unknown" hid every message.
      const said = firstLine(bodyText) || (subject !== "(no subject)" ? subject : "");
      const to = m.last_to ? chatName(m.last_to) : "everyone";
      const summary = `${said || "(empty message)"} — to ${to}`;
      return {
        kind: "message",
        ts: m.last_at || "",
        agent_id: m.last_from || "",
        machine_id: m.last_from_machine || "",
        subsystem: "conversation",
        summary,
        detail: bodyText || subject,
        thread_id: m.context_id || "",
      };
    }),
  );

  // Fold runs of older per-file auto-stubs from one chat into one row.
  decisions.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  let combined = groupDecisionRows(decisions).concat(messages);

  // Client-side search over the LOADED page. The server already filtered
  // plaintext fields via ?q= (so paging is correct); this additionally
  // catches the decrypted subject/summary, which the server can't see.
  const needle = (coordinationState.q || "").toLowerCase().trim();
  if (needle) {
    combined = combined.filter(
      (r) =>
        (r.summary || "").toLowerCase().includes(needle) ||
        (r.agent_id || "").toLowerCase().includes(needle) ||
        (r.machine_id || "").toLowerCase().includes(needle) ||
        (r.subsystem || "").toLowerCase().includes(needle),
    );
  }
  combined.sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  // Controls: search box, date-range select, kind chips. Always rendered
  // (even on empty) so the user can always change the filter.
  const windowSelect = `<select data-coord-window class="filter-chip" style="cursor: pointer;">${WINDOW_OPTIONS.map(
    (o) =>
      `<option value="${o.value}" ${o.value === coordinationState.window ? "selected" : ""}>${o.label}</option>`,
  ).join("")}</select>`;
  const searchBox = `<input data-coord-search type="text" placeholder="Search agent, machine, subject…" value="${escapeHTML(coordinationState.q || "")}" style="background: var(--bg-soft); border: 1px solid var(--border); border-radius: 6px; padding: 4px 10px; font-size: 12px; color: var(--text); min-width: 200px;" />`;
  const chipsHTML = `
    <span style="margin-left: auto; display: flex; gap: 6px; flex-wrap: wrap; align-items: center;">
      ${searchBox}
      ${windowSelect}
      <button class="filter-chip ${activityFilter.kinds.has("decision") ? "active" : ""}" data-activity-kind="decision" data-tooltip="Decisions: one summary per chat session (what it worked on, which files, how long)">${kindCountLabel("decisions", coordinationState.decLoaded, coordinationState.decTotal)}</button>
      <button class="filter-chip ${activityFilter.kinds.has("message") ? "active" : ""}" data-activity-kind="message" data-tooltip="Messages: cross-chat coordination via agc msg send">${kindCountLabel("messages", coordinationState.msgLoaded, coordinationState.msgTotal)}</button>
    </span>`;

  wireCoordControls(body);

  if (combined.length === 0) {
    const pending =
      (activityFilter.kinds.has("decision") && !coordinationState.decLoaded) ||
      (activityFilter.kinds.has("message") && !coordinationState.msgLoaded);
    if (pending) {
      setBodyHTML(
        body,
        '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Loading agent decisions…</div></div>',
        { force },
      );
      return;
    }
    const anyData = coordinationState.decTotal > 0 || coordinationState.msgTotal > 0;
    const title = anyData ? "No activity matches your filters" : "No agent activity recorded yet";
    const detail = anyData
      ? "Try a wider date range, clear the search, or re-enable a kind. There " +
        (coordinationState.decTotal + coordinationState.msgTotal === 1 ? "is 1 row" : "are " + (coordinationState.decTotal + coordinationState.msgTotal) + " rows") +
        " in this window."
      : "Agent activity covers decisions (one summary per chat session: what it worked on, which files, how long) and coordination messages (agc msg send). Activity appears here within ~5s of each event for live machines, or within the hourly rollup for cross-machine totals.";
    const header =
      '<div style="padding: 10px 16px; font-size: 12px; color: var(--text-muted); border-bottom: 1px solid var(--border); display: flex; gap: 16px; align-items: center; flex-wrap: wrap;"><span><strong>0</strong> shown</span>' +
      chipsHTML +
      "</div>";
    setBodyHTML(
      body,
      header +
        '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">' +
        escapeHTML(title) +
        '</div><div class="empty-detail">' +
        escapeHTML(detail) +
        '</div><div style="margin-top: 12px;"><button data-coord-reset class="filter-chip" style="cursor: pointer;">Show all activity</button></div></div>',
      { force },
    );
    return;
  }

  const machineCount = new Set(
    combined.map((r) => r.machine_id).filter(Boolean),
  ).size;
  let html = `
    <div style="padding: 10px 16px; font-size: 12px; color: var(--text-muted); border-bottom: 1px solid var(--border); display: flex; gap: 16px; align-items: center; flex-wrap: wrap;">
      <span><strong>${combined.length}</strong> shown across <strong>${Math.max(1, machineCount)}</strong> machine${Math.max(1, machineCount) === 1 ? "" : "s"}</span>
      ${chipsHTML}
    </div>
    <table class="lease-table coord-table" style="width: 100%;">
      <thead>
        <tr>
          <th style="width: 9%;">Kind</th>
          ${machineCount ? '<th style="width: 12%;">Machine</th>' : ""}
          <th style="width: 14%;">Chat</th>
          <th style="width: 36%;">Summary</th>
          <th style="width: 14%;">Area / Thread</th>
          <th style="width: 16%; text-align: right;">When</th>
        </tr>
      </thead>
      <tbody>`;
  for (const r of combined) {
    const kindChip =
      r.kind === "decision"
        ? '<span class="type-badge tb-heartbeat">decision</span>'
        : '<span class="type-badge tb-cooldown_elapsed">message</span>';
    const rowKey = r.kind + ":" + (r.thread_id || r.lease_id || r.ts + r.summary);
    const clickAttrs =
      r.kind === "message" && r.thread_id
        ? ` data-thread-id="${escapeHTML(r.thread_id)}" style="cursor: pointer;" title="Open the conversation"`
        : ` data-decision-key="${escapeHTML(rowKey)}" style="cursor: pointer;" title="${r.group ? "Show the files" : "Show the full record"}"`;
    html += `
      <tr${clickAttrs}>
        <td data-label="Kind">${kindChip}</td>
        ${machineCount ? `<td data-label="Machine"><code style="font-size: 11px;">${escapeHTML((r.machine_id || "").slice(0, 14) || "—")}</code></td>` : ""}
        <td data-label="Chat" style="font-size: 12px;">${agentCell(r.agent_id)}</td>
        <td data-label="Summary" class="resource-cell">${escapeHTML(r.summary || "—")}</td>
        <td data-label="Area" style="font-size: 11px; color: var(--text-secondary);">${escapeHTML(r.subsystem || "—")}</td>
        <td data-label="When" class="age-cell">${escapeHTML(ago(r.ts))}</td>
      </tr>`;
    if (r.group && openDecisions.has(rowKey)) {
      html += `
      <tr class="coord-detail"><td colspan="${machineCount ? 6 : 5}">
        <div class="coord-detail-meta">${escapeHTML(String(r.count))} files released by ${escapeHTML(r.agent_id || "this chat")}, newest first</div>
        <div class="coord-detail-body">${r.items
          .map((it) => escapeHTML(`${it.path || it.resource} · ${it.purpose || "(no purpose)"}${it.duration ? " · held " + it.duration : ""} · ${ago(it.ts)}`))
          .join("\n")}</div>
      </td></tr>`;
    } else if (r.kind === "decision" && openDecisions.has(rowKey)) {
      html += `
      <tr class="coord-detail"><td colspan="${machineCount ? 6 : 5}">
        <div class="coord-detail-meta">${isSessionRecord(r)
          ? escapeHTML((r.agent_id || "") + (r.duration ? " · ran " + r.duration : ""))
          : escapeHTML(r.resource || "") + (r.duration ? " · held " + escapeHTML(r.duration) : "") + (r.agent_id ? " · " + escapeHTML(r.agent_id) : "")}</div>
        <div class="coord-detail-body">${escapeHTML(readableRecord(r.detail) || "(no write-up)")}</div>
      </td></tr>`;
    }
  }
  html += "</tbody></table>";
  // Per-source pagers (only render when a source is active + has >1 page).
  if (activityFilter.kinds.has("decision")) {
    html += pagerHTML("decision", coordinationState.decPage, coordinationState.decTotal, coordinationState.pageSize);
  }
  if (activityFilter.kinds.has("message")) {
    html += pagerHTML("message", coordinationState.msgPage, coordinationState.msgTotal, coordinationState.pageSize);
  }
  setBodyHTML(body, html, { force });
  wireCoordControls(body);
}

// resetCoordFilters clears every filter back to defaults.
function resetCoordFilters() {
  activityFilter.kinds = new Set(["decision", "message"]);
  coordinationState.q = "";
  coordinationState.window = "168h";
  coordinationState.decPage = 0;
  coordinationState.msgPage = 0;
}

// wireCoordControls attaches the delegated listeners once. Handles kind
// chips, the date-range select, the search input, the pager, the reset
// button, and message-row click-through.
function wireCoordControls(body) {
  if (body._coordWired) return;
  body._coordWired = true;

  body.addEventListener("click", (e) => {
    // Kind chip — refuse to remove the LAST active kind (no dead empty).
    const chip = e.target.closest("[data-activity-kind]");
    if (chip) {
      const k = chip.dataset.activityKind;
      if (activityFilter.kinds.has(k)) {
        if (activityFilter.kinds.size > 1) activityFilter.kinds.delete(k);
      } else {
        activityFilter.kinds.add(k);
      }
      renderCloudDecisions({ force: true, refetch: true });
      return;
    }
    // Reset.
    if (e.target.closest("[data-coord-reset]")) {
      resetCoordFilters();
      renderCloudDecisions({ force: true, refetch: true });
      return;
    }
    // Pager.
    const pager = e.target.closest("[data-coord-page]");
    if (pager) {
      const [kind, action] = pager.dataset.coordPage.split(":");
      const pageKey = kind === "decision" ? "decPage" : "msgPage";
      const total = kind === "decision" ? coordinationState.decTotal : coordinationState.msgTotal;
      const totalPages = Math.max(1, Math.ceil(total / coordinationState.pageSize));
      let p = coordinationState[pageKey] || 0;
      if (action === "first") p = 0;
      else if (action === "prev") p = Math.max(0, p - 1);
      else if (action === "next") p = Math.min(totalPages - 1, p + 1);
      else if (action === "last") p = totalPages - 1;
      coordinationState[pageKey] = p;
      renderCloudDecisions({ force: true, refetch: true });
      return;
    }
    // Message row → its conversation; decision row → its full record inline.
    const row = e.target.closest("[data-thread-id]");
    if (row) { openThreadDrawer(row.dataset.threadId); return; }
    const dec = e.target.closest("[data-decision-key]");
    if (dec) {
      const k = dec.dataset.decisionKey;
      if (openDecisions.has(k)) openDecisions.delete(k);
      else openDecisions.add(k);
      renderCloudDecisions({ force: true });
    }
  });

  // Date-range select.
  body.addEventListener("change", (e) => {
    const sel = e.target.closest("[data-coord-window]");
    if (!sel) return;
    coordinationState.window = sel.value;
    coordinationState.decPage = 0;
    coordinationState.msgPage = 0;
    renderCloudDecisions({ force: true, refetch: true });
  });

  // Search — server-side for plaintext (refetch, reset page) + client-side
  // for the decrypted subject (re-render). Debounced.
  const onSearch = debounce(async (val) => {
    coordinationState.q = val;
    coordinationState.decPage = 0;
    coordinationState.msgPage = 0;
    await renderCloudDecisions({ force: true, refetch: true });
    // The re-render rebuilds the input; restore focus + caret to end so
    // typing isn't interrupted.
    const inp = body.querySelector("[data-coord-search]");
    if (inp) {
      inp.focus();
      const v = inp.value;
      inp.setSelectionRange(v.length, v.length);
    }
  }, 250);
  body.addEventListener("input", (e) => {
    const inp = e.target.closest("[data-coord-search]");
    if (!inp) return;
    onSearch(inp.value);
  });
}

export async function renderDecisionsPage(opts) {
  const body = document.getElementById("decisions-body");
  if (!body) return;
  if (isBodyFirstPaint(body)) {
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Loading agent decisions…</div></div>',
    );
  }
  try {
    await renderDecisionsPageInner(body, opts);
  } catch (err) {
    console.error("[renderDecisionsPage] threw:", err);
    setBodyHTML(
      body,
      '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Agent Decisions page hit an error</div><div class="empty-detail" style="font-family: var(--font-mono); font-size: 12px;">' +
        escapeHTML((err && err.message) || String(err)) +
        "</div></div>",
    );
  }
}


// ---- Collaboration drill-down (Analytics Collaboration card → here) ----

// syncCollabDrill makes the URL and collabDrillState agree. A ?collab= in
// the URL wins (refresh, shared link, Back). Otherwise an active filter
// writes itself back into the URL, which the router resets to /coordination
// on every visit (e.g. Back from an opened thread).
function syncCollabDrill() {
  const cs = collabDrillState;
  const fromURL = parseCollabQuery(location.search);
  if (fromURL) {
    if (!cs.drill || cs.drill.kind !== fromURL.kind || cs.drill.days !== fromURL.days) {
      cs.drill = fromURL;
      cs.page = 0;
      cs.loadedKey = "";
    }
    return;
  }
  if (cs.drill) {
    history.replaceState(history.state, "", routeURL("/coordination" + collabQueryString(cs.drill)));
  }
}

function clearCollabDrill() {
  collabDrillState.drill = null;
  collabDrillState.items = [];
  collabDrillState.loadedKey = "";
  history.replaceState(history.state, "", routeURL("/coordination"));
}

const COLLAB_BADGE = {
  direct: "tb-cooldown_elapsed",
  broadcast: "tb-cooldown_elapsed",
  reply: "tb-cooldown_elapsed",
  note: "tb-released",
  reminder: "tb-expired",
};

async function renderCollabDrill(body, opts) {
  const cs = collabDrillState;
  const keyOf = () => (cs.drill ? cs.drill.kind + "|" + cs.drill.days + "|" + cs.page : "");
  const key = keyOf();
  if (cs.loadedKey !== key) {
    // The render that started the fetch paints when it lands.
    if (cs.loading) return;
    setBodyHTML(
      body,
      `<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Loading ${escapeHTML(collabFilterLabel(cs.drill))}…</div></div>`,
      { force: true },
    );
    await fetchCollabItems();
    // Changed while loading: draw the new filter. Cleared: the feed
    // already owns the body.
    if (keyOf() !== key) {
      if (cs.drill) await renderCollabDrill(body, opts);
      return;
    }
  }
  wireCollabControls(body);
  const force = !!(opts && opts.force);

  const note = collabFilterNote(cs.drill);
  const bar = `
    <div class="collab-filter-bar">
      <span class="collab-filter-label">${escapeHTML(collabFilterLabel(cs.drill))}</span>
      <span class="collab-filter-count"><strong>${cs.total.toLocaleString()}</strong> in all</span>
      <button class="filter-chip" data-collab-clear data-tooltip="Show the whole Coordination feed">Clear</button>
    </div>
    ${note ? `<div class="collab-filter-note">${escapeHTML(note)}</div>` : ""}`;

  if (cs.error) {
    setBodyHTML(
      body,
      bar + `<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Could not load this list</div><div class="empty-detail">${escapeHTML(cs.error)}</div></div>`,
      { force },
    );
    return;
  }
  if (!cs.items.length) {
    setBodyHTML(
      body,
      bar + '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Nothing in this window</div></div>',
      { force },
    );
    return;
  }

  const rows = cs.items.map((it) => collabRow(it, cs.hasText));
  const showMachine = rows.some((r) => r.machine_id);
  let html = bar + `
    <table class="lease-table coord-table" style="width: 100%;">
      <thead>
        <tr>
          <th style="width: 11%;">Kind</th>
          ${showMachine ? '<th style="width: 12%;">Machine</th>' : ""}
          <th style="width: 16%;">Chat</th>
          <th>What</th>
          <th style="width: 14%; text-align: right;">When</th>
        </tr>
      </thead>
      <tbody>`;
  for (const r of rows) {
    const open = r.thread_id
      ? ` data-collab-thread="${escapeHTML(r.thread_id)}" style="cursor: pointer;" title="Open the conversation"`
      : "";
    html += `
      <tr${open}>
        <td data-label="Kind"><span class="type-badge ${COLLAB_BADGE[r.kind] || "tb-released"}">${escapeHTML(r.kind)}</span></td>
        ${showMachine ? `<td data-label="Machine"><code style="font-size: 11px;">${escapeHTML(r.machine_id.slice(0, 14) || "—")}</code></td>` : ""}
        <td data-label="Chat" style="font-size: 12px;">${agentCell(r.agent_id)}</td>
        <td data-label="What" class="resource-cell">${escapeHTML(r.summary)}</td>
        <td data-label="When" class="age-cell">${escapeHTML(ago(r.ts))}</td>
      </tr>`;
  }
  html += "</tbody></table>";
  const pages = Math.max(1, Math.ceil(cs.total / cs.pageSize));
  if (pages > 1) {
    const start = cs.page * cs.pageSize;
    html += `<div class="pagination" style="padding: 8px 16px;">
      <div class="info"><strong style="color: var(--text);">${(start + 1).toLocaleString()}&ndash;${Math.min(start + cs.pageSize, cs.total).toLocaleString()}</strong> of <strong style="color: var(--text);">${cs.total.toLocaleString()}</strong></div>
      <div class="controls">
        <button data-collab-page="prev" ${cs.page <= 0 ? "disabled" : ""}>&lsaquo; Prev</button>
        <span class="page-indicator">${cs.page + 1} / ${pages}</span>
        <button data-collab-page="next" ${cs.page >= pages - 1 ? "disabled" : ""}>Next &rsaquo;</button>
      </div>
    </div>`;
  }
  setBodyHTML(body, html, { force });
}

function wireCollabControls(body) {
  if (body._collabWired) return;
  body._collabWired = true;
  body.addEventListener("click", (e) => {
    if (!collabDrillState.drill) return;
    if (e.target.closest("[data-collab-clear]")) {
      clearCollabDrill();
      renderDecisionsPage({ force: true });
      return;
    }
    const pager = e.target.closest("[data-collab-page]");
    if (pager) {
      const cs = collabDrillState;
      const pages = Math.max(1, Math.ceil(cs.total / cs.pageSize));
      cs.page = pager.dataset.collabPage === "next" ? Math.min(pages - 1, cs.page + 1) : Math.max(0, cs.page - 1);
      renderDecisionsPage({ force: true });
      return;
    }
    const row = e.target.closest("[data-collab-thread]");
    if (row) openThreadDrawer(row.dataset.collabThread);
  });
}

async function renderDecisionsPageInner(_body, opts) {
  syncCollabDrill();
  if (collabDrillState.drill) {
    await renderCollabDrill(_body, opts);
    return;
  }
  // Unified Coordination feed for BOTH local and hosted modes. The fetchers
  // (fetchCoordinationDecisions/Messages) branch on isCloudMode() to hit the
  // right endpoints — local: /v1/decisions + /v1/a2a/threads (plaintext);
  // hosted: /v1/teams/decisions + /v1/teams/handoffs/threads (encrypted
  // hints + BU filter) — and renderCloudDecisions normalizes both shapes
  // into one row model (decisions + messages, search, date-range, server
  // pagination). This replaced the old local-only decisions view so the two
  // dashboards mirror each other.
  await renderCloudDecisions(opts);
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Render now. The feed's data comes from its own fetchers, so waiting
  // for the first dashboard:tick left the page on its placeholder whenever
  // the boot fetch was slow or the team server never answered.
  renderDecisionsPage();
  tickHandler = () => {
    if (state.activePage === "coordination") renderDecisionsPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector(
    'section.page[data-page="coordination"]',
  );
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}

// Export renderCloudDecisions so bootstrap.js can re-export
// it via the AC_LEGACY bridge entry. Eliminated when the bridge
// goes away.
export { renderCloudDecisions };

